import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/database';
import format from 'date-fns/format'
import _ from 'lodash'
import { getCategory, getCompanyCustomQuestions, invalidateCompanyCustomQuestionsCache } from './core/cacheService';
import { apiNodeRequest } from './core/main'

function normalizeCustomRequest(payload: any, fallbackUser: any) {
    if (payload && payload.user) {
        return {
            user: payload.user,
            forceRefresh: !!payload.forceRefresh,
        }
    }

    if (payload && typeof payload === 'object' && payload.forceRefresh !== undefined) {
        return {
            user: fallbackUser,
            forceRefresh: !!payload.forceRefresh,
        }
    }

    return {
        user: payload || fallbackUser,
        forceRefresh: false,
    }
}

function sortCustomQuestions(questions: any[]) {
    return _.sortBy(questions, [function(o:any) { return o.question_en; }])
}

function buildLocalDate(year: number, month: number, day: number) {
    const date = new Date(year, month - 1, day)

    if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
    ) {
        return null
    }

    return date
}

function parseCustomQuestionDate(value: any) {
    if (!value) {
        return null
    }

    if (value instanceof Date) {
        return !isNaN(value.getTime()) ? value : null
    }

    if (typeof value === 'string') {
        const isoDateParts = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
        if (isoDateParts) {
            return buildLocalDate(
                Number(isoDateParts[1]),
                Number(isoDateParts[2]),
                Number(isoDateParts[3])
            )
        }

        const dateParts = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
        if (dateParts) {
            return buildLocalDate(
                Number(dateParts[3]),
                Number(dateParts[2]),
                Number(dateParts[1])
            )
        }
    }

    const date = new Date(value)
    if (!isNaN(date.getTime())) {
        return date
    }

    return null
}

function formatCustomQuestionDate(value: any) {
    const date = parseCustomQuestionDate(value)

    if (!date) {
        return ''
    }

    return format(date, 'DD/MM/YYYY')
}

function customQuestionHasNotExpired(question: any) {
    const endDate = parseCustomQuestionDate(question && question.endon)

    if (!endDate) {
        return true
    }

    return format(endDate, 'YYYY-MM-DD') >= format(new Date(), 'YYYY-MM-DD')
}

function isExcludedCustomQuestionKey(key: any) {
    return key === '-L1u2Xa5_pSCnitgF3u6' ||
        key === '-L1u2Xa5_pSCnitgF3u65' ||
        key === '-L1u93FCwY42UOoIhXPC'
}

function filterCustomQuestionsByStatus(questions: any[], status: string) {
    if (!status || status === 'all') {
        return questions
    }

    return _.filter(questions, (item): any => {
        return item.block && item.block.match(status)
    })
}

function filterCustomQuestionsByText(questions: any[], searchValue: string) {
    if (!searchValue) {
        return questions
    }

    const re = new RegExp(searchValue, 'gi')
    return _.filter(questions, (item): any => {
        if (item.question_en && item.question_en.match(re)) {
            return true
        } else if (item.question && item.question.match(re)) {
            return true
        } else if (item.schedule_name && item.schedule_name.match(re)) {
            return true
        } else if (item.type_name && item.type_name.match(re)) {
            return true
        }
    })
}

function filterCustomQuestionsByOptions(questions: any[], filterData: any) {
    if (!filterData) {
        return questions
    }

    const questionTypes = filterData.questionstype || []
    const powerSkills = filterData.powerskill || []
    const dimensions = filterData.dimension || []
    const activeFilterCount = [questionTypes, powerSkills, dimensions].filter((value) => value.length > 0).length

    if (activeFilterCount === 0) {
        return questions
    }

    const matches: any[] = []
    questionTypes.forEach((value: string) => {
        matches.push(..._.filter(questions, (item) => item.type_name == value))
    })
    dimensions.forEach((value: string) => {
        matches.push(..._.filter(questions, (item) => item.category_name == value))
    })
    powerSkills.forEach((value: string) => {
        matches.push(..._.filter(questions, (item) => item.power_skill == `${value}`))
    })

    const groupedMatches = _.groupBy(matches, function (question) {
        return question.key
    })

    const filteredQuestions: any[] = []
    for (const key in groupedMatches) {
        if (groupedMatches[key].length === activeFilterCount) {
            filteredQuestions.push(groupedMatches[key][0])
        }
    }

    return filteredQuestions
}

function buildVisibleCustomQuestions(state: any, overrides: any = {}) {
    const selectShow = overrides.selectShow !== undefined ? overrides.selectShow : state.selectShow
    const inputSearch = overrides.inputSearch !== undefined ? overrides.inputSearch : state.inputSearch
    const filterEnabled = overrides.filter !== undefined ? overrides.filter : state.filter
    const filterData = overrides.filterData !== undefined ? overrides.filterData : state.filterData

    const statusScopedQuestions = filterCustomQuestionsByStatus(sortCustomQuestions(state.questionsall || []), selectShow)
    const filteredQuestions = filterEnabled
        ? sortCustomQuestions(filterCustomQuestionsByOptions(statusScopedQuestions, filterData))
        : statusScopedQuestions
    const visibleQuestions = sortCustomQuestions(filterCustomQuestionsByText(filteredQuestions, inputSearch))

    return {
        filteredQuestions,
        visibleQuestions,
    }
}

export default {
    namespaced: true,
    state: {
        questions:[],
        questionssearch: [],
        questionsall:[],
        inputSearch: '',
        selectShow: '',
        countquestion: [],
        category: [],
        schedule: {
            'onetime': 'One-time',
            'weekly': 'Weekly',
            'monthly': 'Monthly',
            'quarterly': 'Quarterly',
            'biannually': 'Bi-annually',
            'yearly': 'Yearly',
        },
        filter:false,
        questionsFilter:[],
        filterData:{},
        questionLoading:false,
        getquestionLoading:false
    },
    mutations: {
        setQuestions(state:any, result:any) {
          
            state.questions = result
        },
        setQuestionsSearch(state:any, result:any) {
            state.questionssearch = result
        }, 
        setQuestionsAll(state: any, result: any) {
            state.questionsall = result
        },
        setinputSearch(state:any,value:any) {
            state.inputSearch = value.value
        },
        setselectShow(state: any, value: any) {
            state.selectShow = value.type
        },
        setCountQuestion(state: any, value: any) {
            state.countquestion = value
        },
        setFilter(state:any, result:any) {
            state.filter = result
        },
        setQuestionsFilter(state:any, result:any) {
            state.questionsFilter = result
        },
        setFilterData(state:any, result:any) {
            state.filterData = result
        },
        setQuestionLoading(state:any, value:any) {
            state.questionLoading = value;
        }, 
        setgetquestionLoading(state:any, value:any) {
            state.getquestionLoading = value;
        },
        
    },
    actions: {
        async getQuestionsCustom({commit,dispatch,rootState,state}:any, payload:any){
            const { user = rootState.authen.user, forceRefresh = false } = normalizeCustomRequest(payload, rootState.authen.user)
            if (!user) {
                return []
            }

            if (!forceRefresh && state.questionsall.length > 0) {
                const counts: Array<any> = [];
                counts.push(_.countBy(state.questionsall, 'block'))
                counts[0].all = state.questionsall.length
                const { filteredQuestions, visibleQuestions } = buildVisibleCustomQuestions(state)
                commit('setQuestions', visibleQuestions)
                commit('setQuestionsSearch', visibleQuestions)
                commit('setQuestionsFilter', filteredQuestions)
                commit('setCountQuestion', counts[0])
                commit('setQuestionLoading', false)
                return visibleQuestions
            }

            commit('setQuestionLoading', true)
            let optionTo=rootState.question.optionToName
            if ((!optionTo || Object.keys(optionTo).length === 0) && rootState.authen.user) {
                await dispatch('question/getChildren', null, { root: true })
                await dispatch('question/getTeamAndCohort', null, { root: true })
                optionTo = rootState.question.optionToName
            }
            const usersByEmail: { [key: string]: any } = {}
            const addUserByEmail = (userData: any) => {
                if (!userData || !userData.email) return
                usersByEmail[String(userData.email).trim().toLowerCase()] = userData
            }
            if (rootState.question && Array.isArray(rootState.question.allUsers)) {
                rootState.question.allUsers.forEach(addUserByEmail)
            }
            if (rootState.main && rootState.main.allUser) {
                for (const key in rootState.main.allUser) {
                    addUserByEmail(rootState.main.allUser[key])
                }
            }
            const getUserOptionName = (email: string) => {
                const normalizedEmail = String(email || '').trim().toLowerCase()
                const userData = usersByEmail[normalizedEmail]
                if (!userData) return email || '[Archived Member]'
                const name = `${userData.firstname || ''} ${userData.lastname || ''}`.trim()
                return name || userData.email || email || '[Archived Member]'
            }
            
            const powerupMaster={
                'ps_critical_thinking':{name:'Critical Thinking'},
                'ps_empathy':{name:'Empathy'},
                'ps_initiative_making':{name:'Initiative Making'},
                'ps_leadership':{name:'Leadership'},
                'ps_self_awareness':{name:'Self Awareness'},
                'ps_optimism':{name:'Optimism'}}
            const category:{ [key: string]: string} = {};
            const categoryData=await getCategory()
            for(const key in categoryData){
                if(key) category[key] = categoryData[key].category
            }
            // await firebase.database().ref('category').once('value', snapshot => {
            //     snapshot.forEach((doc):any => {
            //         if(doc.key) category[doc.key] = doc.val().category
            //     }); 
            // });
            const block_questions: { [key: string]: string } = {};
            await firebase.firestore().collection('block_questions').where("company", "==", user.company).get()
            .then(function (querySnapshot) {
                querySnapshot.forEach(function (doc) {
                    if (doc.data().quest_id) block_questions[doc.data().quest_id] = doc.id
                });
            });

            if (forceRefresh) {
                invalidateCompanyCustomQuestionsCache(user.company)
            }

            try {
            const companyQuestionsRaw = await getCompanyCustomQuestions(user.company)
                const questions:Array<any> = [];
                
                Object.keys(companyQuestionsRaw).forEach((questionKey): any => {
                    const questionData = companyQuestionsRaw[questionKey]
                    if(!isExcludedCustomQuestionKey(questionKey) && customQuestionHasNotExpired(questionData)){
                        const question = questionData
                        question.key = questionKey
                        
                        question.category_name = category[questionData.category]
                        question.schedule_name = state.schedule[questionData.repeats]
                        question.powerSkill=question.power_tag?question.power_tag:null
                        if(questionData.parent == 'Y'){
                            question.type_name = questionData.repeats == 'Program-only' ? 'Program-only' : 'Follow-up'
                        }else if(questionData.type == 'Multiple Choice'){
                            question.type_name = 'Yes/No'
                            if(questionData.custom_type=='multiple_choice'){
                                question.type_name = 'Multiple Choice'
                            }
                        }else if(questionData.type == 'Text Area Field'){
                            if(questionData.power_tag){
                                question.power_skill=`${powerupMaster[questionData.power_tag].name}`
                            }else{
                                question.power_skill=''
                            }
                            question.type_name = 'Open-ended'
                        }else{
                            question.type_name = questionData.type
                        }
                        if(questionData.parent=="Y"){
                            question.frequency = questionData.repeats == 'Program-only' ? 'Program-only' : 'Follow-up'
                        }else if(questionData.repeats=="onetime"){
                            question.frequency="One-time"
                        }else if(questionData.repeats=="weekly"){
                            let frequencyDay=0
                            _.forEach(questionData.repeat_days, (value)=> {
                                if(value==="Y")
                                 {frequencyDay++}
                            });

                            if (frequencyDay >= 5) {
                                question.frequency="Daily"
                              } else {
                                question.frequency="Weekly"
                              }

                        }else if(questionData.repeats=="monthly"){
                            if (questionData.repeat_every > 1) {
                                question.frequency="Bi-monthly"
                            }
                            else{
                                question.frequency="Monthly"
                            }
                        }else if(questionData.repeats=="quarterly"){
                            if (questionData.repeat_quarters) {
                                if(questionData.repeat_quarters.Q1=="Y"&&questionData.repeat_quarters.Q2=="Y"&&questionData.repeat_quarters.Q3=="Y"&&questionData.repeat_quarters.Q4=="Y"){
                                    question.frequency="Quarterly"
                                }else{
                                    question.frequency="Bi-annually"
                                }
                            }
                        }else if(questionData.repeats=="biannually"){
                            question.frequency="Bi-annually"
                        }
                        else if(questionData.repeats=="yearly"){
                            question.frequency="Yearly"
                        }
                        question.block_id = (block_questions[question.key]) ? block_questions[question.key]:''
                        question.block = (block_questions[question.key]) ? 'Y' : 'N'
                        question.block_status = (block_questions[question.key]) ? false : true
                       
                        const optionToName:Array<any>=[]
                        const questionUsers = Array.isArray(question.users)
                            ? question.users
                            : (question.users ? Object.values(question.users) : [])
                        if(!question.teams&&!question.cohorts&&!questionUsers.length){
                            optionToName.push("All teams and cohorts")
                        }
                         if(question.teams){
                            question.teams.map(value=>{
                                optionToName.push(optionTo[value] || "[Archived Team]")
                            })
                        }
                        if(question.cohorts){
                            question.cohorts.map(value=>{
                                optionToName.push(optionTo[value] || "[Archived Cohort]")
                            })
                        }
                        if(questionUsers.length){
                            questionUsers.map((value:any)=>{
                                optionToName.push(getUserOptionName(value))
                            })
                        }
                        question.optionToName=optionToName
                       
                        questions.push(question)
                    }
                });
                const sortedQuestions = sortCustomQuestions(questions)
                commit('setQuestionsAll', sortedQuestions)
                const counts: Array<any> = [];
                counts.push(_.countBy(sortedQuestions, 'block'))
                counts[0].all = sortedQuestions.length
                const { filteredQuestions, visibleQuestions } = buildVisibleCustomQuestions({
                    ...state,
                    questionsall: sortedQuestions,
                })
                commit('setQuestions', visibleQuestions)
                commit('setQuestionsSearch', visibleQuestions)
                commit('setQuestionsFilter', filteredQuestions)
                commit('setCountQuestion', counts[0])
                return visibleQuestions
            } catch(error) {
                console.log(error)
                return []
            } finally {
                commit('setQuestionLoading', false)
            }
        },
        searchQuestions({ commit, rootState, dispatch,state}:any,value:string){
            const { visibleQuestions } = buildVisibleCustomQuestions(state, {
                inputSearch: value,
            })
            commit('setQuestionsSearch', visibleQuestions)
            commit('setQuestions', visibleQuestions)
        },
        selectStatus({ commit, rootState, dispatch, state }: any, value: string) {
            const { visibleQuestions } = buildVisibleCustomQuestions(state, {
                selectShow: value,
            })
            commit('setQuestions', visibleQuestions)
            commit('setQuestionsSearch', visibleQuestions)
        },
        async blockQuestion({ commit, rootState, dispatch }: any, key: any) {
            if (rootState.authen.user.user_type === 'leadership') {
                return
            }
            const querySnapshot = await firebase.firestore().collection('block_questions').where("company", "==", rootState.authen.user.company).where("quest_id", "==", key).get()
            if(querySnapshot.empty){
                const block_questions: { [key: string]: string } = {};
                block_questions['quest_id'] = key
                block_questions['company'] = rootState.authen.user.company
                block_questions['email_updated'] = rootState.authen.user.email
                block_questions['createdate'] = format(new Date(), 'YYYY-MM-DD HH:mm:ss')
                const docRef = await firebase.firestore().collection('block_questions').add(block_questions)
                try {
                    /*save logs*/
                    await dispatch('authen/addLogs', {
                        company: rootState.authen.user.company,
                        createdate: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
                        id: docRef.id,
                        table: 'block_questions',
                        status: 'block',
                        user: rootState.authen.user.email
                    }, {root:true})
                } catch (error) {
                    console.error('Error logging custom question block:', error)
                }
            }
            await dispatch('getQuestionsCustom',{ user: rootState.authen.user, forceRefresh: true })
        },
        async unblockQuestion({ commit, rootState, state, dispatch }: any, key: any) {
            if (rootState.authen.user.user_type === 'leadership') {
                return
            }
            const querySnapshot = await firebase.firestore().collection('block_questions').where("company", "==", rootState.authen.user.company).where("quest_id", "==", key).get()
            const deletePromises: Array<any> = []
            querySnapshot.forEach(function (doc) {
                deletePromises.push(doc.ref.delete())
            });
            await Promise.all(deletePromises)

            const questions = state.questions;
            for(const x in questions) {
                if(questions[x].key === key) {
                    questions[x].block_id = '';
                    questions[x].block = 'N';
                    questions[x].block_status = true;
                    break;
                }
            }
            //commit('setQuestionsAll', questions)
            // commit('setQuestions', questions)

            if (!querySnapshot.empty) {
                try {
                    /*save logs*/
                    await dispatch('authen/addLogs', {
                        company: rootState.authen.user.company,
                        createdate: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
                        id: key,
                        table: 'block_questions',
                        status: 'unblock',
                        user: rootState.authen.user.email
                    }, {root:true})
                } catch (error) {
                    console.error('Error logging custom question unblock:', error)
                }
            }
            await dispatch('getQuestionsCustom',{ user: rootState.authen.user, forceRefresh: true })
        },
        async CreateUpdateQuestion({commit,rootState,state,dispatch}:any,value:any){
           
            
            let optionToType=rootState.question.optionToType
            let team:Array<any>=[]
            let cohorts:Array<any>=[]
            let users:Array<any>=[]
            let data: { [key: string]: any; } = {};
            let formData = _.cloneDeep(value.form);
            let alluser = _.find(formData.cascaderTo, function(item){
                return item && item[0] == 'all_teams_and_cohorts';
            });
            if(!alluser){
                formData.cascaderTo.map(value=>{
                    const selectedValue = value && value[0]
                    if(!selectedValue) return
                    if(optionToType[selectedValue]=='team'){
                        team.push(selectedValue)
                    }
                    if(optionToType[selectedValue]=='cohorts'){
                        cohorts.push(selectedValue)
                    }
                    if(String(selectedValue).indexOf('user_') === 0){
                        users.push(String(selectedValue).replace(/^user_/, ''))
                    }
                })
            }
            data = {
                category: formData.category,
                cohorts:cohorts,
                teams:team,
                users:users,
                collect_anonymous_feedback:formData.showResultsInDaily==true?false:true,
                genus: "coins",
                question: (!formData.questions.th || formData.questions.th.trim() === '')?formData.questions.en:formData.questions.th,
                question_en: formData.questions.en,
                questions:formData.questions,
                type: formData.type,
                repeats: formData.frequency,
                status: (formData.status)?formData.status: 'N',
                parent: 'N',
                company: rootState.authen.user.company,
                updated: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
                custom_type:formData.custom_type!=undefined? formData.custom_type:formData.type == 'Multiple Choice'?'yes_no':formData.type == 'Multiple Choice(other)'?'multiple_choice':formData.type == 'Scale'?"scale":'open_ended',
                power_tag:formData.type == 'Text Area Field'?(formData.powerSkill?formData.powerSkill:null):null,
                rolling: formData.rolling?formData.rolling:false,
            };
            
            if(!data.questions.th){
                data.questions.th=data.questions.en
            }
            if(formData.type !== 'Text Area Field'){
                if(formData.report_card == true){
                    data.report_card = 'Y'
                    data.report_card_label = formData.report_card_label
                }else{
                    data.report_card = null
                    data.report_card_label = null
                }
            }else{
                data.report_card = null
                data.report_card_label = null
            }
            if(formData.culture_index == true){
                data.culture_index = true
            }else{
                data.culture_index = null
            }
            if(data.collect_anonymous_feedback==false){
                delete data.collect_anonymous_feedback
            }
            if(alluser){
                // RTDB update() is shallow — omitting keys leaves old teams/cohorts in place.
                data.teams = null
                data.cohorts = null
                data.users = null
            }
            let choice:any = {};
            let m_choice:any = "";
            let choice_th:any = [];
            let choice_en:any = [];
            let choice_points:any = [];
            let choice_weight:any = [];
            let choice_feeling:any = [];
            let choice_quest_id:any = [];
            if(formData.type == 'Multiple Choice'){
                let standard = {
                    great: 'answered yes',
                    great_num: '70',
                    great_question: '',
                    okay: 'answered yes',
                    okay_num: '50',
                    okay_question: '',
                    notgood: 'answered yes',
                    notgood_num: '50',
                    notgood_question: '',
                };
                _.forEach(formData.options_en, (value,key)=> {
                    // choice_en.push(value.value);
                    //choice_weight.push(value.weight);
                    choice_points.push(formData.coins);
                   
                    choice_quest_id.push(value.question_id);

                });
                // _.forEach(formData.options_th, (value,key)=> {
                //     choice_th.push(value.value);
                // });

                for(const i in rootState.reward.language){
             

                    if(rootState.reward.language[i].code=="en"||rootState.reward.language[i].code=="th"){
                        _.forEach(formData[`options_${rootState.reward.language[i].code}`], (value,key)=> {
                           
                          
                                if(!choice[rootState.reward.language[i].code]){
                                    choice[rootState.reward.language[i].code]=[]
                                }
                                choice[rootState.reward.language[i].code].push(value.value)
                                
                               
                            
                        });
                        const first_language=_.find(rootState.reward.language,function(o){return o.code=='th'})
                        if(rootState.reward.language.length==1||!first_language){
                            if(!choice['th']){
                                choice['th']=[]
                            }
                            choice['th'].push("ใช่")
                            choice['th'].push("ไม่ใช่")
                        }

                    }else{
                        _.forEach(formData.options_YN[`${rootState.reward.language[i].code}`], (value,key)=> {
                          
                          
                                if(!choice[rootState.reward.language[i].code]){
                                    choice[rootState.reward.language[i].code]=[]
                                }
                                choice[rootState.reward.language[i].code].push(value.value)
                            
                        });
                    }
                    
    
                
                }
                choice_weight=formData.feelingY==="Negative"?[0,1]:[1,0];
                choice_feeling=[formData.feelingY,formData.feelingN]
                
                m_choice = {
                    ...choice,
                    points: choice_points,
                    weight: choice_weight,
                    feeling:choice_feeling,
                    quest_id: choice_quest_id,
                }
                data.m_choice = m_choice;
                data.standard = standard;
                data.neutral = formData.feelingY==="Neutral"?"Y":"N";
                data.custom_type='yes_no'
                //data.flip=formData.feelingY==="Negative"?"Y":"N";
            }else if(formData.type==="Multiple Choice(other)"){
                let standard = {
                    great: 'answered yes',
                    great_num: '70',
                    great_question: '',
                    okay: 'answered yes',
                    okay_num: '50',
                    okay_question: '',
                    notgood: 'answered yes',
                    notgood_num: '50',
                    notgood_question: '',
                };
                var neutral=_.filter(
                    formData.options,
                    function (currentObject) {
                        return currentObject.feeling === "Neutral";
                    }
                );
                let firstText = true;
                let text='responded'
                _.forEach(formData.options, (value,i:any)=> {
                    let weight:any
                    
                    if(value.feeling==="Positive")
                    {
                        weight=1
                        if (firstText) {
                            firstText = false;
                            text = text+ " “" + value.en
                        } else {
                            text = text + '” or “' + value.en
                        }
                        
                    }else if(value.feeling==="Neutral"&&neutral.length==formData.options.length){
                        if(i==0 || i=='0')
                        {
                            weight=1
                            text = text+ " “" + value.en
                        }else {
                            weight=0
                        }
                    }
                    else{
                        weight=0
                    }
                    choice_en.push(value.en);
                    choice_weight.push(weight);
                    choice_points.push(formData.coins);
                    choice_th.push(value.th?value.th:value.en);
                    choice_feeling.push(value.feeling)
                    choice_quest_id.push(value.question_id?value.question_id:'');
                    for(const i in rootState.reward.language){
                        if(value[rootState.reward.language[i].code]){
                            if(!choice[rootState.reward.language[i].code]){
                                choice[rootState.reward.language[i].code]=[]
                            }
                            choice[rootState.reward.language[i].code].push(value[rootState.reward.language[i].code])
                            if(rootState.reward.language[i].code=='en'){
                                const first_language=_.find(rootState.reward.language,function(o){return o.code=='th'})
                                if(rootState.reward.language.length==1||!first_language){
                                    if(!choice['th']){
                                        choice['th']=[]
                                    }
                                    choice['th'].push(value.en)
                                }
                            }
                        }

                    }
                    

                });
             
                m_choice = {
                    ...choice,
                    points: choice_points,
                    weight: choice_weight,
                    feeling:choice_feeling,
                    quest_id: choice_quest_id,
                }
                data.m_choice = m_choice;
                data.standard = standard;
                data.type='Multiple Choice';
                data.neutral = (neutral.length==formData.options.length)?"Y":"N";
                data.custom_type='multiple_choice'
                standard.great=text+"”"
                standard.okay=text+"”"
                standard.notgood=text+"”"
            }else if(formData.type == 'Scale'){
                let standard={
                    great : "average rating for the work-life balance in your team",
                    great_num : "7.5",
                    great_question : "",
                    notgood : "average rating for the work-life balance in your team",
                    notgood_num : "5",
                    notgood_question : "",
                    okay : "average rating for the work-life balance in your team",
                    okay_num : "5",
                    okay_question : ""
                  }
                data.less_txt=formData.less_txts.th?formData.less_txts.th:formData.less_txts.en;
                data.less_txt_en=formData.less_txts.en;
                data.less_txts=formData.less_txts;
                data.max_txt=formData.max_txts.th?formData.max_txts.th:formData.max_txts.en;
                data.max_txt_en=formData.max_txts.en;
                data.max_txts=formData.max_txts
                data.points = formData.coins;
                data.neutral = "N";
                data.standard = standard;
                data.custom_type='scale'
               
                
            }else{
                data.points = formData.coins;
                data.neutral = "N";
                data.custom_type='open_ended'
            }


            if(formData.frequency == 'onetime'){
                data.starton = formatCustomQuestionDate(formData.schedule);
                if (!data.starton) {
                    throw new Error('Invalid one-time question schedule date')
                }
            }else if(formData.frequency !== 'Follow-up' && formData.frequency !== 'Program-only'){
                let repeat_days:any = "";
                repeat_days = {
                    Sun: (formData.dayofweek && formData.dayofweek == 'sunday')?"Y":"N",
                    Mon: (formData.dayofweek && formData.dayofweek == 'monday')?"Y":"N",
                    Tue: (formData.dayofweek && formData.dayofweek == 'tuesday')?"Y":"N",
                    Wed: (formData.dayofweek && formData.dayofweek == 'wednesday')?"Y":"N",
                    Thu: (formData.dayofweek && formData.dayofweek == 'thursday')?"Y":"N",
                    Fri: (formData.dayofweek && formData.dayofweek == 'friday')?"Y":"N",
                    Sat: (formData.dayofweek && formData.dayofweek == 'saturday')?"Y":"N",
                }
                data.repeat_days = repeat_days;
                data.starton = format(new Date(), 'DD/MM/YYYY');
            }
            if (formData.frequency == 'monthly') {
                let repeat_weeks:any = "";
                repeat_weeks = {
                    week1: (formData.schedule == 'week1')?"Y":"N",
                    week2: (formData.schedule == 'week2')?"Y":"N",
                    week3: (formData.schedule == 'week3')?"Y":"N",
                    week4: (formData.schedule == 'week4')?"Y":"N",
                    week5: "N"
                }
                data.repeat_weeks = repeat_weeks;
            }else if(formData.frequency == 'quarterly'){
                let scheduleQuarter = formData.schedule.split("_");
                let repeat_weeks:any = "";
                repeat_weeks = {
                    week1: (scheduleQuarter[1] == 'week1')?"Y":"N",
                    week2: (scheduleQuarter[1] == 'week2')?"Y":"N",
                    week3: (scheduleQuarter[1] == 'week3')?"Y":"N",
                    week4: (scheduleQuarter[1] == 'week4')?"Y":"N",
                    week5: "N"
                }
                data.repeat_weeks = repeat_weeks;
                data.repeat_quarter=scheduleQuarter[0]
                data.repeat_quarters = {
                    Q1:'Y',
                    Q2:'Y',
                    Q3:'Y',
                    Q4:'Y',
                }
            }else if(formData.frequency == 'biannually'){
                let scheduleQuarter = formData.schedule.split("_");
                let repeat_weeks:any = '';
                repeat_weeks = {
                    week1: (scheduleQuarter[1] == 'week1')?'Y':'N',
                    week2: (scheduleQuarter[1] == 'week2')?'Y':'N',
                    week3: (scheduleQuarter[1] == 'week3')?'Y':'N',
                    week4: (scheduleQuarter[1] == 'week4')?'Y':'N',
                    week5: (scheduleQuarter[1] == 'week5')?'Y':'N',
                }
                data.repeat_weeks = repeat_weeks;
                data.repeat_quarter=scheduleQuarter[0]
                data.repeat_quarters = {
                    Q1:(scheduleQuarter[2] == 'q1')?'Y':'N',
                    Q2:(scheduleQuarter[2] == 'q2')?'Y':'N',
                    Q3:(scheduleQuarter[3] == 'q3')?'Y':'N',
                    Q4:(scheduleQuarter[3] == 'q4')?'Y':'N',
                }
            }else if(formData.frequency == 'yearly'){
                let scheduleQuarter = formData.schedule.split("_");
                let repeat_weeks:any = '';
                repeat_weeks = {
                    week1: (scheduleQuarter[1] == 'week1')?'Y':'N',
                    week2: (scheduleQuarter[1] == 'week2')?'Y':'N',
                    week3: (scheduleQuarter[1] == 'week3')?'Y':'N',
                    week4: (scheduleQuarter[1] == 'week4')?'Y':'N',
                    week5: (scheduleQuarter[1] == 'week5')?'Y':'N',
                }
                data.repeat_weeks = repeat_weeks;
                data.repeat_months=scheduleQuarter[0]
            }else if(formData.frequency == 'Follow-up'){
                data.parent='Y'
                delete data.repeats
                delete data.report_card
                delete data.report_card_label
                delete data.rolling

            }else if(formData.frequency == 'Program-only'){
                // Program-only questions are parked off the public schedule
                // (parent='Y'), but keep repeats='Program-only' as a
                // discriminator so they stay out of the follow-up picker
                // and surface only in the Programs library.
                data.parent='Y'
                data.repeats='Program-only'
                delete data.report_card
                delete data.report_card_label
                delete data.rolling

            }




            if(formData.frequency == 'weekly' || formData.frequency == 'monthly'){
                data.repeat_every = "1"
            }
            for(const i in rootState.reward.language){
                if(!data.questions[rootState.reward.language[i].code]){
                    data.questions[rootState.reward.language[i].code]= data.questions.en?data.questions.en:''
                } 
            }

    
            let newId:any = "";
            let status = "";
            if(formData.key){
                newId = formData.key
                status = 'edit';
                //console.log("data",data)
                await firebase.database().ref(`questions/${newId}`).update(data);
            }else{
                newId = firebase.database().ref('questions').push().key;
                status = 'add';
                let insert = {};
                insert[newId] = data;
                 //console.log('insert',insert)
                await firebase.database().ref('questions').update(insert);
            }
            // console.log('newId',newId)
            
            /*save logs*/
            try {
                await dispatch('authen/addLogs', {
                    company: rootState.authen.user.company,
                    createdate: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
                    id: newId,
                    table: 'questions',
                    status: status,
                    user: rootState.authen.user.email
                }, {root:true})
            } catch (error) {
                console.error('Error logging custom question update:', error)
            }
            await dispatch('getQuestionsCustom',{ user: rootState.authen.user, forceRefresh: true })
            return {
                id: newId,
                status: status,
                question: {
                    ...data,
                    key: newId,
                },
            }
        },
        async deleteQuestion({commit,rootState,dispatch}:any,id:any){
            if(id){
                await firebase.database().ref('questions').child(id).remove();
                /*save logs*/
                try {
                    await dispatch('authen/addLogs', {
                        company: rootState.authen.user.company,
                        createdate: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
                        id: id,
                        table: 'questions',
                        status: 'remove',
                        user: rootState.authen.user.email
                    }, {root:true})
                } catch (error) {
                    console.error('Error logging custom question delete:', error)
                }
                await dispatch('getQuestionsCustom',{ user: rootState.authen.user, forceRefresh: true })
            }
        },
        filtersQuestion({ commit, rootState, dispatch,state}:any,value:any){
            commit('setQuestionLoading', true)
            const nextFilterData = {
                questionstype:value.questionstype,
                powerskill:value.powerskill,
                dimension:value.dimension
            }
            const hasFilters = value.powerskill.length > 0 || value.questionstype.length > 0 || value.dimension.length > 0
            commit('setFilter',hasFilters)
            commit('setFilterData',nextFilterData)
            const { filteredQuestions, visibleQuestions } = buildVisibleCustomQuestions(state, {
                filter: hasFilters,
                filterData: nextFilterData,
            })
            commit('setQuestions', visibleQuestions)
            commit('setQuestionsFilter', filteredQuestions)
            commit('setQuestionLoading', false)



        },
        async getOpenai({ commit, rootState, dispatch,state}:any,value:any){
            // console.log('questions',value.questions)
            // console.log('language',value.languages)
            commit('setgetquestionLoading',true)
            let length_lang=_.clone(value.languages).length
            let languages=''
            let count=0
            const materlanguage=(await firebase.database().ref(`/language`).once('value')).val()
            for(const i in value.languages){
                
                count++
                if(materlanguage[value.languages[i].code]){
                    
                    if(length_lang==count){
                        languages=languages+materlanguage[value.languages[i].code].name

                    }else{
                        languages=languages+materlanguage[value.languages[i].code].name+","
                    }
                    

                }
                // languages
                
            }
            //console.log('languages',languages)
            const config:any = {
                method: 'post',
                url: '/openai/get_question_ai',
                params: {
                    question: value.questions,
                    lang: `[${languages.toString()}]`,
                },
              };
                const response = await apiNodeRequest(config)
                //console.log('response',response.data)
                let message:any=response.data
                const newId = firebase.database().ref('questions').push().key;
                message['key']=newId
                commit('setgetquestionLoading',false)
                return message
        }
    },
    getters: {
        questions : (state:any) => {
            return state.questions
        },
        countquestion: (state: any) => {
            return state.countquestion
        },
        inputSearch: (state:any) => {
            return state.inputSearch
        },
        questioncustomAll: (state: any) => {
            return state.questionsall
        },
        getquestionLoading: (state: any) => {
            return state.getquestionLoading
        }

    }
  }
